import React, { useState } from 'react'
import { connect } from 'react-redux'
import { Alert, Checkbox, List, Modal, notification, Space, Upload } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Button, Typography } from 'modules/core/components'
import configuration from 'modules/configuration'
import { notifyMappingError } from '../../../utils'

const ImportRowsModal = ({
  visible,
  onClose,
  onRefetch,
  mapping,
  importDimensionMappingRows,
  downloadDimensionMappingImportTemplate,
}) => {
  const [file, setFile] = useState(null)
  const [replace, setReplace] = useState(false)
  const [rowErrors, setRowErrors] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const { t } = useTranslation()

  const handleClose = () => {
    setFile(null)
    setReplace(false)
    setRowErrors([])
    setIsSaving(false)
    onClose()
  }

  const handleTemplate = () => {
    downloadDimensionMappingImportTemplate(mapping.source_dimension).catch(notifyMappingError)
  }

  const handleImport = () => {
    setIsSaving(true)
    setRowErrors([])
    importDimensionMappingRows(mapping.source_dimension, file, replace)
      .then((response) => {
        notification.success({
          message: t('CONFIG_DIMENSION_MAPPING_IMPORT_SUCCESS', {
            count: response.payload.data.created,
          }),
        })
        onRefetch()
        handleClose()
      })
      .catch((error) => {
        setIsSaving(false)
        const errors = error?.response?.data?.errors
        if (Array.isArray(errors) && errors.length > 0) {
          setRowErrors(errors)
          return
        }
        notifyMappingError(error)
      })
  }

  const renderRowError = ({ row, errors }) => (
    <List.Item>
      <Typography.Body level={3}>
        {t('CONFIG_DIMENSION_MAPPING_IMPORT_ERROR_ROW', { row })}:{' '}
        {Object.entries(errors)
          .map(([column, messages]) => `${column} — ${messages.join(', ')}`)
          .join(' | ')}
      </Typography.Body>
    </List.Item>
  )

  return (
    <Modal
      title={t('CONFIG_DIMENSION_MAPPING_IMPORT_MODAL_TITLE')}
      visible={visible}
      onCancel={handleClose}
      onOk={handleImport}
      okText={t('ACTION_UPLOAD')}
      cancelText={t('ACTION_CANCEL')}
      okButtonProps={{ disabled: !file }}
      confirmLoading={isSaving}
      destroyOnClose
      centered
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Body level={3}>
          {t(
            mapping?.is_subtype
              ? 'CONFIG_DIMENSION_MAPPING_SUBTYPE_IMPORT_HELP'
              : 'CONFIG_DIMENSION_MAPPING_IMPORT_HELP',
          )}
        </Typography.Body>

        <Button.LinkWhitLeftIcon
          title="CONFIG_DIMENSION_MAPPING_IMPORT_TEMPLATE_ACTION"
          onClick={handleTemplate}
        />

        <Upload
          accept=".xlsx"
          maxCount={1}
          beforeUpload={(selected) => {
            setFile(selected)
            setRowErrors([])
            return false
          }}
          onRemove={() => setFile(null)}
          fileList={file ? [file] : []}
        >
          <Button.Default title="ACTION_SELECT" icon={<UploadOutlined />} />
        </Upload>

        <Checkbox checked={replace} onChange={(e) => setReplace(e.target.checked)}>
          {t('CONFIG_DIMENSION_MAPPING_IMPORT_REPLACE')}
        </Checkbox>

        {rowErrors.length > 0 && (
          <>
            <Alert type="error" message={t('CONFIG_DIMENSION_MAPPING_IMPORT_ERRORS')} />
            <List
              size="small"
              dataSource={rowErrors}
              renderItem={renderRowError}
              style={{ maxHeight: 200, overflowY: 'auto' }}
            />
          </>
        )}
      </Space>
    </Modal>
  )
}

const mapDispatchToProps = {
  importDimensionMappingRows: configuration.actions.importDimensionMappingRows,
  downloadDimensionMappingImportTemplate:
    configuration.actions.downloadDimensionMappingImportTemplate,
}

export default connect(null, mapDispatchToProps)(ImportRowsModal)
